import React, { useEffect, useMemo, useState } from 'react';
import { Download, X } from 'lucide-react';
import type { VaultDocumentDto } from '../../api/cms';
import { fetchVaultFileBlob, triggerBlobDownload } from '../../api/vault';
import { useUiSound } from '../../hooks/useUiSound';
import {
  defaultDownloadOptions,
  DOWNLOAD_FORMAT_OPTIONS,
  DOWNLOAD_SIZE_OPTIONS,
  formatBytes,
  getDownloadSizeHint,
  needsTransformPreview,
  parseTargetSizeKbInput,
  prepareVaultDownload,
  supportsDownloadTransforms,
  type VaultDownloadFormat,
  type VaultDownloadOptions,
  type VaultDownloadSize,
} from '../../lib/vaultDownload';

interface VaultDownloadDialogProps {
  document: VaultDocumentDto | null;
  onClose: () => void;
}

type ProgressView =
  | { label: string; percent: number; indeterminate?: false }
  | { label: string; indeterminate: true };

function DownloadProgressBar({ progress }: { progress: ProgressView }) {
  return (
    <div
      className="wfx-download-progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={progress.indeterminate ? undefined : progress.percent}
      aria-label={progress.label}
    >
      <div className="wfx-download-progress-head">
        <span>{progress.label}</span>
        {!progress.indeterminate && <strong>{progress.percent}%</strong>}
      </div>
      <div className={`wfx-download-progress-track${progress.indeterminate ? ' is-indeterminate' : ''}`}>
        <span style={progress.indeterminate ? undefined : { width: `${progress.percent}%` }} />
      </div>
    </div>
  );
}

export function VaultDownloadDialog({ document, onClose }: VaultDownloadDialogProps) {
  const { play } = useUiSound();
  const [options, setOptions] = useState<VaultDownloadOptions>(() => defaultDownloadOptions(document?.mimeType));
  const [pdfSizeInput, setPdfSizeInput] = useState('');
  const [sourceBlob, setSourceBlob] = useState<Blob | null>(null);
  const [previewSize, setPreviewSize] = useState<string | null>(null);
  const [fetchProgress, setFetchProgress] = useState<number | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<{ percent: number; label: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canTransform = supportsDownloadTransforms(document?.mimeType);

  const effectiveOptions = useMemo<VaultDownloadOptions>(() => ({
    ...options,
    pdfTargetSizeKb: parseTargetSizeKbInput(pdfSizeInput),
  }), [options, pdfSizeInput]);

  const instantHint = useMemo(() => {
    if (!sourceBlob) return null;
    return getDownloadSizeHint(sourceBlob, document?.mimeType, effectiveOptions);
  }, [sourceBlob, document?.mimeType, effectiveOptions]);

  const progressView = useMemo<ProgressView | null>(() => {
    if (fetchProgress != null) {
      return { label: 'Loading file', percent: fetchProgress };
    }
    if (downloadProgress) {
      return { label: downloadProgress.label, percent: downloadProgress.percent };
    }
    return null;
  }, [fetchProgress, downloadProgress]);

  useEffect(() => {
    if (!document) {
      setSourceBlob(null);
      return;
    }

    if (document.fileAvailable === false) {
      setSourceBlob(null);
      setFetchProgress(null);
      setError('This file is no longer available. Use Replace to upload it again.');
      return;
    }

    setOptions(defaultDownloadOptions(document.mimeType));
    setPdfSizeInput('');

    let cancelled = false;
    (async () => {
      setFetchProgress(0);
      setError(null);
      try {
        const blob = await fetchVaultFileBlob(document.id, (percent) => {
          if (!cancelled) setFetchProgress(percent);
        });
        if (!cancelled) setSourceBlob(blob);
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load file.');
      } finally {
        if (!cancelled) setFetchProgress(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [document]);

  useEffect(() => {
    if (!document || !sourceBlob) {
      setPreviewSize(null);
      return;
    }

    setPreviewSize(instantHint);

    if (!canTransform || !needsTransformPreview(effectiveOptions)) {
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const prepared = await prepareVaultDownload(
          sourceBlob,
          document.name,
          document.mimeType,
          effectiveOptions,
        );
        if (!cancelled) setPreviewSize(prepared.sizeLabel);
      } catch {
        if (!cancelled) setPreviewSize(instantHint);
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [document, sourceBlob, effectiveOptions, canTransform, instantHint]);

  if (!document) return null;

  const handleDownload = async () => {
    if (!sourceBlob) return;

    if (options.format === 'pdf' && pdfSizeInput.trim() && effectiveOptions.pdfTargetSizeKb == null) {
      setError('Enter a valid file size in KB (e.g. 500).');
      play('warning');
      return;
    }

    setDownloadProgress({ percent: 0, label: 'Preparing download' });
    setError(null);
    try {
      const prepared = await prepareVaultDownload(
        sourceBlob,
        document.name,
        document.mimeType,
        canTransform ? effectiveOptions : defaultDownloadOptions(document.mimeType),
        ({ percent, label }) => {
          setDownloadProgress({ percent, label: label || 'Preparing download' });
        },
      );
      triggerBlobDownload(prepared.blob, prepared.fileName);
      play('download');
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Download failed.');
      play('error');
    } finally {
      setDownloadProgress(null);
    }
  };

  const formatOptions = canTransform
    ? DOWNLOAD_FORMAT_OPTIONS
    : DOWNLOAD_FORMAT_OPTIONS.filter((opt) => opt.id === 'original');

  const showsImageSizeOptions =
    options.format === 'jpeg'
    || options.format === 'png'
    || options.format === 'webp';

  const pdfSizeInvalid = options.format === 'pdf'
    && pdfSizeInput.trim().length > 0
    && effectiveOptions.pdfTargetSizeKb == null;

  const isBusy = fetchProgress != null || downloadProgress != null;

  return (
    <div className="wfx-vault-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-vault-modal glass-surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vault-download-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-vault-modal-head">
          <div>
            <h2 id="vault-download-title">Download options</h2>
            <p className="wfx-muted">{document.name}</p>
          </div>
          <button type="button" className="ghost-icon-button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>

        {error && <div className="auth-error" role="alert">{error}</div>}

        {progressView && <DownloadProgressBar progress={progressView} />}

        {!canTransform ? (
          <p className="wfx-muted wfx-download-note">
            This file type downloads as originally uploaded.
          </p>
        ) : (
          <div className={`wfx-download-options${isBusy ? ' is-busy' : ''}`}>
            <fieldset className="wfx-download-fieldset" disabled={isBusy}>
              <legend>Format</legend>
              <div className="wfx-download-choice-grid">
                {formatOptions.map((opt) => (
                  <label key={opt.id} className={`wfx-download-choice${options.format === opt.id ? ' is-active' : ''}`}>
                    <input
                      type="radio"
                      name="download-format"
                      checked={options.format === opt.id}
                      onChange={() => setOptions((prev) => ({ ...prev, format: opt.id as VaultDownloadFormat }))}
                    />
                    <strong>{opt.label}</strong>
                    {opt.hint && <span>{opt.hint}</span>}
                  </label>
                ))}
              </div>
            </fieldset>

            {options.format === 'pdf' && (
              <fieldset className="wfx-download-fieldset" disabled={isBusy}>
                <legend>File size</legend>
                <label className="wfx-download-size-input">
                  <span>Target size (KB)</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    placeholder="e.g. 500"
                    value={pdfSizeInput}
                    onChange={(e) => setPdfSizeInput(e.target.value)}
                    aria-invalid={pdfSizeInvalid}
                    disabled={isBusy}
                  />
                </label>
                <p className="wfx-muted wfx-download-note">
                  {sourceBlob
                    ? `Uploaded file: ${formatBytes(sourceBlob.size)}. Leave blank to download at original size.`
                    : 'Leave blank to download at original size.'}
                </p>
                {pdfSizeInvalid && (
                  <p className="auth-error" role="alert">Enter a valid size in KB.</p>
                )}
              </fieldset>
            )}

            {showsImageSizeOptions && (
              <fieldset className="wfx-download-fieldset" disabled={isBusy}>
                <legend>Size</legend>
                <div className="wfx-download-choice-grid">
                  {DOWNLOAD_SIZE_OPTIONS.map((opt) => (
                    <label key={opt.id} className={`wfx-download-choice${options.size === opt.id ? ' is-active' : ''}`}>
                      <input
                        type="radio"
                        name="download-size"
                        checked={options.size === opt.id}
                        onChange={() => setOptions((prev) => ({ ...prev, size: opt.id as VaultDownloadSize }))}
                      />
                      <strong>{opt.label}</strong>
                      <span>{opt.hint}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {(options.format === 'jpeg' || options.format === 'webp') && (
              <label className="wfx-vault-select">
                <span>Quality — {Math.round(options.quality * 100)}%</span>
                <input
                  type="range"
                  min={0.4}
                  max={1}
                  step={0.05}
                  value={options.quality}
                  onChange={(e) => setOptions((prev) => ({ ...prev, quality: Number(e.target.value) }))}
                  disabled={isBusy}
                />
              </label>
            )}

            {document.mimeType === 'application/pdf' && options.format !== 'original' && options.format !== 'pdf' && (
              <p className="wfx-muted wfx-download-note">
                PDF exports use the first page when converting to an image format.
              </p>
            )}
          </div>
        )}

        {!progressView && previewSize && (
          <p className="wfx-download-estimate">Estimated size: {previewSize}</p>
        )}

        <div className="wfx-vault-modal-actions">
          <button type="button" className="secondary-action" onClick={onClose} disabled={isBusy}>Cancel</button>
          <button
            type="button"
            className="primary-action"
            onClick={handleDownload}
            disabled={isBusy || !sourceBlob || pdfSizeInvalid}
          >
            <Download size={16} /> Download
          </button>
        </div>
      </div>
    </div>
  );
}
